<script setup lang="ts">
/**
 * Form 45 (공통 최신본, 2026-10) — Consent to Act as Director and Statement of Non Disqualification
 * (Companies Act 1967, s.173C(a)). DocScope 로 디렉터별 반복. 템플릿: SG\프리셋\Form 45 (공통 최신본)\Form 45.docx
 *
 * 최신본은 예전 조건부 조항(*(iv) 1993년 이전 · *(ba) 노미니 · *(bb) Employment Pass · (c)(ii) 통역)이 빠졌고,
 * 이사 표에 생년월일·이메일·휴대폰, 증인(Notary Public / Witness) 이름·주소, "Dated this" 한 칸이 생겼다.
 * 템플릿 키: director_address(거주) · director_alternate_address(연락) · director_date_of_birth ·
 *   director_email · director_mobile_no · witness_name · witness_address · execution_date · [[Signers]](이사 본인).
 */
import CddVarField from './cdd/CddVarField.vue';
import './allotDoc.css';
</script>

<template>
  <div class="allot-doc f45">
    <div class="doc-head" style="text-align:center">
      <div class="doc-meta">THE COMPANIES ACT 1967</div>
      <div class="doc-meta">SECTION 173C(a)</div>
      <div class="doc-lbl-title" style="margin-top:10px">
        CONSENT TO ACT AS DIRECTOR AND STATEMENT OF NON DISQUALIFICATION TO ACT AS DIRECTOR
      </div>
      <div class="doc-company" style="margin-top:8px; letter-spacing:.2em">FORM 45</div>
    </div>

    <p class="doc-p">Name of Company: <CddVarField field-key="company_name" label="Company Name" /></p>
    <p class="doc-p">Company No: <CddVarField field-key="company_registration_no" label="Company Registration No." /></p>

    <p class="doc-p" style="margin-top:12px">
      I, the undermentioned person, hereby consent to act as a director of the abovenamed company with effect from
      <CddVarField field-key="appointment_effective_date" label="Appointment Effective Date" />
      (date) and declare that:
    </p>

    <p class="doc-p doc-clause">(a) I am not disqualified from acting as a director, in that:</p>
    <p class="doc-p doc-subclause">(i) I am not below 18 years of age and that I am otherwise of full legal capacity.</p>
    <p class="doc-p doc-subclause">
      (ii) Within a period of 3 years preceding the date of this statement I have not had any disqualification order
      made by the General Division of the High Court of Singapore against me under section 149A(1) of the Companies Act
      1967 (“the Act”), section 60(1) of the Limited Liability Partnerships Act 2005 (“the LLP Act”) or section 57(1)
      of the Variable Capital Companies Act 2018 (“the VCC Act”).
    </p>
    <p class="doc-p doc-subclause">
      (iii) Within a period of 5 years preceding the date of this statement I have not had any disqualification order
      made by the General Division of the High Court of Singapore against me under section 149(1) or 154(2) of the Act,
      section 59(1) or 61(2) of the LLP Act, or section 56(1) or 58(2) of the VCC Act.
    </p>

    <p class="doc-p doc-subclause">(v) That within a period of 5 years preceding the date of this statement, I have not been —</p>
    <p class="doc-p doc-subclause">
      (A) convicted of any offence, whether in Singapore or elsewhere, involving fraud or dishonesty punishable with
      imprisonment for 3 months or more;
    </p>
    <p class="doc-p doc-subclause">
      (B) convicted of any offence under Part 12 of the Securities and Futures Act 2001, where the conviction was on
      or after 1 July 2015;
    </p>
    <p class="doc-p doc-subclause">
      (C) convicted of an offence under section 50, 51, 53, 54, 55 or 55A of the Corruption, Drug Trafficking and
      Other Serious Crimes (Confiscation of Benefits) Act 1992, where the conviction is on or after 6 May 2026;
    </p>
    <p class="doc-p doc-subclause">
      (D) imposed a civil penalty under section 232 of the Securities and Futures Act 2001 on or after 1 July 2015;
    </p>
    <p class="doc-p doc-subclause">
      (E) removed from an office or employment as a director or executive officer of a holder of a capital markets
      services licence pursuant to a direction under section 97(1A) of the Securities and Futures Act 2001;
    </p>
    <p class="doc-p doc-subclause">
      (F) removed as a director, executive officer or chief executive officer of a person mentioned in section 99(1)
      of the Securities and Futures Act 2001, in compliance with a condition imposed on the person under section
      99(4) of that Act; and
    </p>
    <p class="doc-p doc-subclause">(G) subject to a prohibition order (as defined in section 2(1) of the VCC Act).</p>

    <p class="doc-p doc-subclause">(vi) That —</p>
    <p class="doc-p doc-subclause">
      (A) I have not been convicted of 3 or more offences under the Act in relation to any requirement to file with,
      deliver or send any return, account or other document to the Registrar, or give notice of any matter to the
      Registrar; or if I have been convicted for such offences, the last of such convictions did not take place during
      the period of 5 years preceding the date of this statement;
    </p>
    <p class="doc-p doc-subclause">
      (B) I have not had 3 or more orders of the General Division of the High Court of Singapore made against me under
      section 13 or 399 of the Act in relation to any requirement to file with, deliver or send any return, account or
      other document to the Registrar, or give notice of any matter to the Registrar; or if I have had such orders made
      against me, the last of those orders was not made during the period of 5 years preceding the date of this
      statement;
    </p>
    <p class="doc-p doc-subclause">(C) I am not an undischarged bankrupt under section 148(1) of the Act;</p>
    <p class="doc-p doc-subclause">
      (D) I have not been a director of 3 or more companies which names had been struck off the register under section
      344 of the Act within a period of 5 years preceding the date of this statement; or I have been a director of 3 or
      more such companies but one of the following applies:
    </p>
    <p class="doc-p doc-subclause">
      (DA) I had not been a director of at least 3 of those companies at the time their names were so struck off;
    </p>
    <p class="doc-p doc-subclause">
      (DB) the period during which I was disqualified from acting as a director of a company under section 155A(1) of
      the Act has expired;
    </p>
    <p class="doc-p doc-subclause">
      (E) there is currently no debarment order under section 155B of the Act or section 59 of the VCC Act (read with
      section 155B of the Act) against me;
    </p>
    <p class="doc-p doc-subclause">
      (F) I have not been convicted of 3 or more offences under the VCC Act in relation to any requirement to file
      with, deliver or send any return, account or other document to the Registrar of Variable Capital Companies, or
      give notice of any matter to that Registrar; or if I have been convicted for such offences, the last of such
      convictions did not take place during the period of 5 years preceding the date of this statement;
    </p>
    <p class="doc-p doc-subclause">
      (G) I have not had 3 or more orders of the General Division of the High Court of Singapore made against me under
      section 12 or 160 of the VCC Act in relation to any requirement to file with, deliver or send any return,
      account or other document to the Registrar of Variable Capital Companies, or give notice of any matter to that
      Registrar, or if I have had such orders made against me, the last of those orders was not made during the period
      of 5 years preceding the date of this statement; and
    </p>
    <p class="doc-p doc-subclause">
      (H) I have not been a director of a variable capital company that has been struck off under section 344 of the
      Act (as applied by section 130B of the VCC Act) at the time when the name of the variable capital company had
      been struck off the register; or I have been a director of such variable capital company (“VCC A”) at the time
      the name of VCC A had been struck off the register but one of the following applies:
    </p>
    <p class="doc-p doc-subclause">
      (HA) I was not, within a period of 5 years immediately before the date on which the name of VCC A was struck off
      the register, a director of not less than 2 other variable capital companies the names of which have been struck
      off the register;
    </p>
    <p class="doc-p doc-subclause">
      (HB) I was, within a period of 5 years immediately before the date on which the name of VCC A was struck off the
      register a director of not less than 2 other variable capital companies the names of which have been struck off
      the register, but I was not a director of at least 2 of such variable capital companies at the time their names
      were so struck off;
    </p>
    <p class="doc-p doc-subclause">
      (HC) the period during which I was disqualified from acting as a director of a variable capital company under
      section 60(1) of the VCC Act has expired.
    </p>
    <p class="doc-p doc-subclause">
      (vii) By virtue of the foregoing I am not disqualified from acting as a director of the abovenamed company.
    </p>

    <p class="doc-p doc-clause">
      (b) I am aware of and undertake to abide by my duties, responsibilities and liabilities specified in the Act as
      well as under the common law where applicable, including the following key administrative and substantive
      duties, that is, to:
    </p>
    <p class="doc-p doc-subclause">(i) discharge my responsibilities in the company;</p>
    <p class="doc-p doc-subclause">
      (ii) ensure that I have a reasonable degree of skill and knowledge to handle the affairs of the company;
    </p>
    <p class="doc-p doc-subclause">
      (iii) act honestly and be reasonably diligent in discharging my duties and act in the interest of the company
      without putting myself in a position of conflict of interest;
    </p>
    <p class="doc-p doc-subclause">
      (iv) employ the powers and assets that I am entrusted with for the proper purposes of the company and not for
      any collateral purpose;
    </p>
    <p class="doc-p doc-subclause">
      (v) ensure that the company and I comply with all the requirements and obligations under the Act including those
      in respect of meetings, requisitions, resolutions, accounts, reports, statements, records and other documents on
      the company, filing and notices and any other prerequisites; and
    </p>
    <p class="doc-p doc-subclause">
      (vi) account to the shareholders for my conduct of the affairs of the company and make such disclosures that are
      incumbent upon me under the Act.
    </p>

    <p class="doc-p doc-clause">
      (bc) By executing this form, I acknowledge that I am aware of my obligations under the Act and undertake to use
      this company only for legitimate business purposes in compliance with all applicable laws. I understand that any
      misuse of this company for unlawful purposes, including money laundering, the commission of fraud, the conduct of
      scams or the carrying out of activities prejudicial to the public peace, welfare or good order in Singapore may
      result in regulatory or legal action.
    </p>

    <p class="doc-p doc-clause">(c) That —</p>
    <p class="doc-p doc-subclause">(i) I have read and understood the above statements</p>
    <p class="doc-p">
      before I executed this form and I confirm that the statements are true. I am also aware that I can be prosecuted
      in Court if I willfully give any information on this form which is false.
    </p>

    <table class="cdd-tbl f45-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Name:</td><td colspan="3"><CddVarField field-key="director_name" label="Director Name" /></td></tr>
        <tr><td class="cdd-lcell">Residential address:</td><td colspan="3"><CddVarField field-key="director_address" label="Residential Address" /></td></tr>
        <tr><td class="cdd-lcell">Contact address:</td><td colspan="3"><CddVarField field-key="director_alternate_address" label="Contact Address" /></td></tr>
        <tr>
          <td class="cdd-lcell">* Nric /Fin/ Passport No:</td><td><CddVarField field-key="director_identification_no" label="NRIC / FIN / Passport No." /></td>
          <td class="cdd-lcell">Nationality:</td><td><CddVarField field-key="director_nationality" label="Nationality" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Date of birth:</td><td><CddVarField field-key="director_date_of_birth" label="Date of Birth" /></td>
          <td class="cdd-lcell">Email address:</td><td><CddVarField field-key="director_email" label="Email Address" /></td>
        </tr>
        <tr><td class="cdd-lcell">Mobile phone number:</td><td colspan="3"><CddVarField field-key="director_mobile_no" label="Mobile Phone Number (+country code)" /></td></tr>
      </tbody>
    </table>

    <p class="doc-p f45-sign">Signature: <span class="f45-sign-note">(the director above signs via T-Sign)</span></p>

    <p class="doc-p">Signed in the presence of * Notary Public/ Witness:</p>
    <table class="cdd-tbl f45-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Signature:</td><td><span class="f45-sign-note">(signed by hand)</span></td></tr>
        <tr><td class="cdd-lcell">Name:</td><td><CddVarField field-key="witness_name" label="Witness Name" /></td></tr>
        <tr><td class="cdd-lcell">Address:</td><td><CddVarField field-key="witness_address" label="Witness Address" /></td></tr>
        <tr><td class="cdd-lcell">Dated this</td><td><CddVarField field-key="execution_date" label="Dated" /></td></tr>
      </tbody>
    </table>
    <div class="doc-attn"><p>*Delete where inapplicable</p></div>
  </div>
</template>

<style scoped>
.f45-tbl { margin-top: 10px; }
.f45-sign { margin-top: 14px; text-align: right; }
.f45-sign-note { color: #8a97b5; font-size: 11px; font-style: italic; }
.doc-attn p { margin: 0; }
</style>
